import React, { useEffect, useState, useCallback } from "react"
import axios from "axios"
import { useUI } from '../contexts/UIContext'

const API = process.env.REACT_APP_API_URL || "http://localhost:3001/api"
const ARQUIVOS_BASE = API.replace(/\/api\/?$/, '')

const STATUS_COR: any = {
  pendente_revisao: { bg: "#451a03", border: "#f59e0b", text: "#fbbf24", label: "⏳ Pendente" },
  anomalia:          { bg: "#450a0a", border: "#dc2626", text: "#f87171", label: "⚠️ Anomalia" },
  aprovado:          { bg: "#14532d", border: "#22c55e", text: "#4ade80", label: "✅ Aprovado" },
  rejeitado:         { bg: "#1e293b", border: "#64748b", text: "#94a3b8", label: "🚫 Rejeitado" },
}

const CONFIANCA_COR: any = {
  alta: "#4ade80",
  media: "#fbbf24",
  baixa: "#f87171",
}

const fmt = (n: any) => (n !== null && n !== undefined) ? `R$ ${parseFloat(n).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}` : "—"
const fmtLitros = (n: any) => (n !== null && n !== undefined) ? `${parseFloat(n).toLocaleString("pt-BR", { minimumFractionDigits: 2 })} L` : "—"
const fmtKm = (n: any) => (n !== null && n !== undefined) ? `${Math.round(parseFloat(n)).toLocaleString("pt-BR")} km` : "—"
const fmtDataHora = (d: any) => d ? new Date(d).toLocaleString("pt-BR") : "—"
const fmtDataRecibo = (d: any) => {
  if (!d) return null
  const soData = String(d).split('T')[0] // aceita tanto 'YYYY-MM-DD' quanto ISO completo vindo do Postgres
  const dt = new Date(soData + 'T12:00:00')
  return isNaN(dt.getTime()) ? null : dt.toLocaleDateString('pt-BR')
}

const LABEL_COMBUSTIVEL: any = {
  gasolina: 'Gasolina', etanol: 'Etanol', diesel: 'Diesel', diesel_s10: 'Diesel S10', gnv: 'GNV', nao_informado: 'Não informado',
}
const ICONE_COMBUSTIVEL: any = {
  gasolina: '⛽', etanol: '🌱', diesel: '🚛', diesel_s10: '🚛', gnv: '💨', nao_informado: '❓',
}

const OPCOES_COMBUSTIVEL = [
  { valor: 'gasolina', label: 'Gasolina' },
  { valor: 'etanol', label: 'Etanol' },
  { valor: 'diesel', label: 'Diesel' },
  { valor: 'diesel_s10', label: 'Diesel S10' },
  { valor: 'gnv', label: 'GNV' },
]

export default function Abastecimento() {
  const { toast, confirm } = useUI()
  const [lista, setLista] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [filtroStatus, setFiltroStatus] = useState("")
  const [detalhe, setDetalhe] = useState<any>(null)
  const [processando, setProcessando] = useState(false)
  const [editandoCombustivel, setEditandoCombustivel] = useState(false)
  const [estatisticas, setEstatisticas] = useState<any>(null)
  const [fechamento, setFechamento] = useState<any>(null)
  const [fechando, setFechando] = useState(false)

  const token = localStorage.getItem('moobi_token')
  const headers = { Authorization: `Bearer ${token}` }

  const carregar = useCallback(async () => {
    setLoading(true)
    try {
      const params: any = {}
      if (filtroStatus) params.status = filtroStatus
      const { data } = await axios.get(`${API}/abastecimentos`, { headers, params })
      setLista(data)
    } catch {
      toast('Erro ao carregar abastecimentos', 'error')
    } finally {
      setLoading(false)
    }
  }, [filtroStatus]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { carregar() }, [carregar])

  const buscarEstatisticas = () => {
    axios.get(`${API}/abastecimentos/estatisticas`, { headers })
      .then(({ data }) => setEstatisticas(data))
      .catch(() => {})
  }

  const buscarFechamento = useCallback(() => {
    const agora = new Date()
    axios.get(`${API}/abastecimentos/fechamento`, { headers, params: { ano: agora.getFullYear(), mes: agora.getMonth() + 1 } })
      .then(({ data }) => setFechamento(data))
      .catch(() => {})
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { buscarEstatisticas(); buscarFechamento() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fecharMes = async () => {
    if (!fechamento) return
    const ok = await confirm({
      mensagem: `Fechar o mês ${String(fechamento.mes).padStart(2, '0')}/${fechamento.ano}?`,
      detalhe: 'Depois de fechado, os abastecimentos desse mês não poderão mais ser editados ou revisados.',
      tipo: 'warning',
    })
    if (!ok) return
    setFechando(true)
    try {
      await axios.post(`${API}/abastecimentos/fechamento`, { ano: fechamento.ano, mes: fechamento.mes }, { headers })
      toast('Mês fechado com sucesso', 'success')
      buscarFechamento()
    } catch (e: any) {
      toast(e?.response?.data?.erro || 'Erro ao fechar o mês', 'error')
    } finally {
      setFechando(false)
    }
  }

  const revisar = async (id: string, acao: 'aprovar' | 'rejeitar') => {
    if (acao === 'rejeitar') {
      const ok = await confirm({ mensagem: 'Rejeitar este abastecimento?', detalhe: 'Essa ação não pode ser desfeita.', tipo: 'danger' })
      if (!ok) return
    }
    setProcessando(true)
    try {
      await axios.put(`${API}/abastecimentos/${id}/revisar`, { acao }, { headers })
      toast(acao === 'aprovar' ? 'Abastecimento aprovado' : 'Abastecimento rejeitado', 'success')
      setDetalhe(null)
      carregar()
      buscarEstatisticas()
      buscarFechamento()
    } catch {
      toast('Erro ao processar revisão', 'error')
    } finally {
      setProcessando(false)
    }
  }

  const salvarCombustivel = async (valor: string) => {
    try {
      const { data } = await axios.put(`${API}/abastecimentos/${detalhe.id}`, { combustivel_tipo: valor }, { headers })
      setDetalhe((prev: any) => ({ ...prev, ...data }))
      setEditandoCombustivel(false)
      toast('Combustível atualizado', 'success')
      carregar()
    } catch {
      toast('Erro ao atualizar combustível', 'error')
    }
  }

  const contagem = {
    pendente_revisao: lista.filter(a => a.status === 'pendente_revisao').length,
    anomalia: lista.filter(a => a.status === 'anomalia').length,
  }

  const agora = new Date()
  const doMes = lista.filter(a => {
    const dataRef = a.data_recibo || a.data_hora
    const d = new Date(dataRef)
    return d.getMonth() === agora.getMonth() && d.getFullYear() === agora.getFullYear() && a.status !== 'rejeitado'
  })
  const totalGastoMes = doMes.reduce((s, a) => s + (parseFloat(a.valor_total) || 0), 0)
  const totalLitrosMes = doMes.reduce((s, a) => s + (parseFloat(a.litros) || 0), 0)
  const mediaValorLitro = totalLitrosMes > 0 ? totalGastoMes / totalLitrosMes : 0

  return (
    <div style={{ padding: 24, height: '100%', overflowY: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: '#f1f5f9', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="ph-bold ph-gas-pump" style={{ color: '#0ea5e9' }} />
            Controle de Abastecimento
          </h1>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
            {contagem.pendente_revisao > 0 && <span style={{ color: '#fbbf24' }}>{contagem.pendente_revisao} pendente(s) · </span>}
            {contagem.anomalia > 0 && <span style={{ color: '#f87171' }}>{contagem.anomalia} com anomalia</span>}
            {contagem.pendente_revisao === 0 && contagem.anomalia === 0 && 'Tudo revisado'}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 6 }}>
          {['', 'pendente_revisao', 'anomalia', 'aprovado', 'rejeitado'].map(s => (
            <button key={s} onClick={() => setFiltroStatus(s)}
              style={{
                background: filtroStatus === s ? '#1e3a6e' : 'transparent',
                border: '1px solid #2d4166', borderRadius: 6, color: filtroStatus === s ? '#38bdf8' : '#94a3b8',
                cursor: 'pointer', fontSize: 11, padding: '6px 10px', fontWeight: 600,
              }}>
              {s === '' ? 'Todos' : STATUS_COR[s]?.label || s}
            </button>
          ))}
        </div>
      </div>

      {/* Fechamento Mensal */}
      {fechamento && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
          background: fechamento.fechado ? '#0d2e1a' : (fechamento.pendentes > 0 ? '#451a03' : '#0c2a4a'),
          border: `1px solid ${fechamento.fechado ? '#166534' : (fechamento.pendentes > 0 ? '#92400e' : '#0ea5e9')}`,
          borderRadius: 10, padding: '10px 16px', marginBottom: 16,
        }}>
          <div style={{ fontSize: 12, color: fechamento.fechado ? '#4ade80' : (fechamento.pendentes > 0 ? '#fbbf24' : '#38bdf8'), display: 'flex', alignItems: 'center', gap: 6 }}>
            <i className={`ph-bold ${fechamento.fechado ? 'ph-lock-simple' : 'ph-lock-simple-open'}`} />
            {fechamento.fechado
              ? `Mês ${String(fechamento.mes).padStart(2, '0')}/${fechamento.ano} fechado em ${new Date(fechamento.fechadoEm).toLocaleString('pt-BR')} por ${fechamento.fechadoPorNome || '—'}`
              : fechamento.pendentes > 0
                ? `${fechamento.pendentes} abastecimento(s) pendente(s) de revisão em ${String(fechamento.mes).padStart(2, '0')}/${fechamento.ano} — revise todos para liberar o fechamento`
                : `Mês ${String(fechamento.mes).padStart(2, '0')}/${fechamento.ano} pronto para fechar — todos os abastecimentos foram revisados`}
          </div>
          {!fechamento.fechado && fechamento.pendentes === 0 && (
            <button onClick={fecharMes} disabled={fechando}
              style={{ background: '#0ea5e9', border: 'none', borderRadius: 6, color: '#fff', fontWeight: 700, fontSize: 12, padding: '6px 14px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              {fechando ? 'Fechando...' : 'Fechar mês'}
            </button>
          )}
        </div>
      )}

      {/* Cards de resumo do mês */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 20 }}>
        <div style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 10, padding: '12px 16px' }}>
          <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1 }}>Gasto no mês</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#4ade80', marginTop: 4 }}>{fmt(totalGastoMes)}</div>
        </div>
        <div style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 10, padding: '12px 16px' }}>
          <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1 }}>Litros no mês</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#38bdf8', marginTop: 4 }}>{fmtLitros(totalLitrosMes)}</div>
        </div>
        <div style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 10, padding: '12px 16px' }}>
          <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1 }}>Preço médio/L</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#f1f5f9', marginTop: 4 }}>{fmt(mediaValorLitro)}</div>
        </div>
        <div style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 10, padding: '12px 16px' }}>
          <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1 }}>Abastecimentos no mês</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#f1f5f9', marginTop: 4 }}>{doMes.length}</div>
        </div>
      </div>

      {/* Eficiência km/L */}
      {estatisticas && estatisticas.totalRegistrosConsiderados > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
            Eficiência (km/L) — calculado entre abastecimentos consecutivos
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
            <div style={{ background: '#0c2a4a', border: '1px solid #0ea5e9', borderRadius: 10, padding: '12px 16px' }}>
              <div style={{ fontSize: 10, color: '#7dd3fc', textTransform: 'uppercase', letterSpacing: 1 }}>Média geral</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#38bdf8', marginTop: 4 }}>
                {estatisticas.mediaGeral != null ? `${estatisticas.mediaGeral.toFixed(2)} km/L` : '—'}
              </div>
            </div>
            {estatisticas.porCombustivel.map((c: any) => (
              <div key={c.tipo} style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 10, padding: '12px 16px' }}>
                <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1 }}>
                  {ICONE_COMBUSTIVEL[c.tipo] || ''} {LABEL_COMBUSTIVEL[c.tipo] || c.tipo}
                </div>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#f1f5f9', marginTop: 4 }}>{c.mediaKmL.toFixed(2)} km/L</div>
                <div style={{ fontSize: 10, color: '#475569', marginTop: 2 }}>{c.registros} registro(s)</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading && <div style={{ textAlign: 'center', color: '#64748b', padding: '60px 0' }}>Carregando...</div>}

      {!loading && lista.length === 0 && (
        <div style={{ textAlign: 'center', color: '#64748b', padding: '60px 0' }}>
          <i className="ph-bold ph-tray" style={{ fontSize: 32, display: 'block', marginBottom: 8 }} />
          Nenhum abastecimento encontrado
        </div>
      )}

      {!loading && lista.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
          {lista.map(ab => {
            const cor = STATUS_COR[ab.status] || STATUS_COR.pendente_revisao
            return (
              <div key={ab.id} onClick={() => { setDetalhe(ab); setEditandoCombustivel(false) }}
                style={{
                  background: '#0f172a', border: `1px solid ${cor.border}44`, borderRadius: 10,
                  padding: 14, cursor: 'pointer', transition: 'border-color .15s',
                }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#f1f5f9' }}>{ab.ativo_nome}</div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>{ab.ativo_placa}</div>
                  </div>
                  <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 8, background: cor.bg, color: cor.text, whiteSpace: 'nowrap' }}>
                    {cor.label}
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, fontSize: 11, color: '#94a3b8' }}>
                  <div><i className="ph-bold ph-drop" style={{ marginRight: 4 }} />{fmtLitros(ab.litros)}</div>
                  <div><i className="ph-bold ph-currency-circle-dollar" style={{ marginRight: 4 }} />{fmt(ab.valor_total)}</div>
                  <div><i className="ph-bold ph-gauge" style={{ marginRight: 4 }} />{fmtKm(ab.km_informado)}</div>
                  <div><i className="ph-bold ph-calendar-check" style={{ marginRight: 4 }} />{fmtDataRecibo(ab.data_recibo) || fmtDataHora(ab.data_hora)}</div>
                </div>
                {ab.motivo_anomalia && (
                  <div style={{ fontSize: 10, color: '#f87171', marginTop: 8, background: '#450a0a33', padding: '4px 6px', borderRadius: 5 }}>
                    {ab.motivo_anomalia}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Modal de detalhe */}
      {detalhe && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setDetalhe(null)}>
          <div style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 10, width: 640, maxWidth: '94vw', maxHeight: '90vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid #1e293b', position: 'sticky', top: 0, background: '#0f172a' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#f1f5f9' }}>{detalhe.ativo_nome} — {detalhe.ativo_placa}</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>
                  Abastecido em {fmtDataRecibo(detalhe.data_recibo) || '—'}
                  {' · '}registrado {fmtDataHora(detalhe.data_hora)} por {detalhe.usuario_nome || '—'}
                  {detalhe.origem === 'manual' && ' · 🧾 lançamento manual (feito pelo painel)'}
                </div>
              </div>
              <button onClick={() => setDetalhe(null)}
                style={{ background: 'rgba(0,0,0,0.3)', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 16, borderRadius: 6, padding: '2px 6px' }}>
                <i className="ph-bold ph-x" style={{ fontSize: 13 }} />
              </button>
            </div>

            <div style={{ padding: 18 }}>
              {detalhe.motivo_anomalia && (
                <div style={{ background: '#450a0a', border: '1px solid #7f1d1d', borderRadius: 8, padding: '10px 12px', marginBottom: 14, fontSize: 12, color: '#f87171' }}>
                  <i className="ph-bold ph-warning" style={{ marginRight: 6 }} />{detalhe.motivo_anomalia}
                </div>
              )}

              {/* Fotos */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 10, color: '#64748b', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1 }}>Recibo</div>
                  <a href={`${ARQUIVOS_BASE}${detalhe.foto_recibo_url}`} target="_blank" rel="noreferrer">
                    <img src={`${ARQUIVOS_BASE}${detalhe.foto_recibo_url}`} alt="Recibo"
                      style={{ width: '100%', borderRadius: 8, border: '1px solid #1e293b', maxHeight: 260, objectFit: 'cover' }} />
                  </a>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: '#64748b', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1 }}>Odômetro</div>
                  {detalhe.foto_odometro_url ? (
                  <a href={`${ARQUIVOS_BASE}${detalhe.foto_odometro_url}`} target="_blank" rel="noreferrer">
                    <img src={`${ARQUIVOS_BASE}${detalhe.foto_odometro_url}`} alt="Odômetro"
                      style={{ width: '100%', borderRadius: 8, border: '1px solid #1e293b', maxHeight: 260, objectFit: 'cover' }} />
                  </a>
                  ) : (
                    <div style={{ border: '1px dashed #334155', borderRadius: 8, padding: 16, fontSize: 12, color: '#64748b', textAlign: 'center' }}>Foto do odômetro não enviada (lançamento manual)</div>
                  )}
                </div>
              </div>

              {/* Dados extraídos */}
              <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 8, padding: 12, marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1 }}>Dados do recibo (lidos por IA)</div>
                  {detalhe.dados_extraidos_ia?.confianca && (
                    <span style={{ fontSize: 10, color: CONFIANCA_COR[detalhe.dados_extraidos_ia.confianca] || '#94a3b8' }}>
                      confiança {detalhe.dados_extraidos_ia.confianca}
                    </span>
                  )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12 }}>
                  <div><span style={{ color: '#64748b' }}>Posto:</span> <span style={{ color: '#e2e8f0' }}>{detalhe.posto_nome || '—'}</span></div>
                  <div><span style={{ color: '#64748b' }}>Combustível:</span>{' '}
                    {editandoCombustivel ? (
                      <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                        <select autoFocus defaultValue={detalhe.combustivel_tipo || ''}
                          onChange={e => salvarCombustivel(e.target.value)}
                          onBlur={() => setEditandoCombustivel(false)}
                          style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 4, color: '#e2e8f0', fontSize: 11, padding: '2px 4px' }}>
                          <option value="">— selecione —</option>
                          {OPCOES_COMBUSTIVEL.map(o => <option key={o.valor} value={o.valor}>{o.label}</option>)}
                        </select>
                      </span>
                    ) : (
                      <span style={{ color: '#e2e8f0', cursor: 'pointer', borderBottom: '1px dashed #475569' }}
                        onClick={() => setEditandoCombustivel(true)} title="Clique para editar">
                        {OPCOES_COMBUSTIVEL.find(o => o.valor === detalhe.combustivel_tipo)?.label || detalhe.combustivel_tipo || '— definir'}
                        <i className="ph-bold ph-pencil-simple" style={{ fontSize: 10, marginLeft: 4, color: '#64748b' }} />
                      </span>
                    )}
                  </div>
                  <div><span style={{ color: '#64748b' }}>Litros:</span> <span style={{ color: '#e2e8f0' }}>{fmtLitros(detalhe.litros)}</span></div>
                  <div><span style={{ color: '#64748b' }}>Valor/L:</span> <span style={{ color: '#e2e8f0' }}>{fmt(detalhe.valor_litro)}</span></div>
                  <div><span style={{ color: '#64748b' }}>Total:</span> <span style={{ color: '#4ade80', fontWeight: 700 }}>{fmt(detalhe.valor_total)}</span></div>
                  <div><span style={{ color: '#64748b' }}>KM informado:</span> <span style={{ color: '#e2e8f0' }}>{fmtKm(detalhe.km_informado)}</span></div>
                </div>
                {detalhe.dados_extraidos_ia?.observacao && (
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8, fontStyle: 'italic' }}>
                    {detalhe.dados_extraidos_ia.observacao}
                  </div>
                )}
              </div>

              {/* Ações */}
              {['pendente_revisao', 'anomalia'].includes(detalhe.status) && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => revisar(detalhe.id, 'aprovar')} disabled={processando}
                    style={{ flex: 1, padding: '10px', borderRadius: 8, border: 'none', background: '#22c55e', color: '#052e16', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                    <i className="ph-bold ph-check-circle" style={{ marginRight: 6 }} />Aprovar
                  </button>
                  <button onClick={() => revisar(detalhe.id, 'rejeitar')} disabled={processando}
                    style={{ flex: 1, padding: '10px', borderRadius: 8, border: '1px solid #7f1d1d', background: 'transparent', color: '#f87171', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                    <i className="ph-bold ph-x-circle" style={{ marginRight: 6 }} />Rejeitar
                  </button>
                </div>
              )}
              {!['pendente_revisao', 'anomalia'].includes(detalhe.status) && (
                <div style={{ fontSize: 11, color: '#64748b', textAlign: 'center' }}>
                  Revisado {detalhe.revisado_em ? 'em ' + fmtDataHora(detalhe.revisado_em) : ''}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
